import React, { useState, useEffect } from 'react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Ticket, Plus, Pencil, Trash2, Euro, Calendar, Users, AlertCircle, CheckCircle } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const TicketManager = ({ eventId, eventTitle }) => {
    const [tickets, setTickets] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [editingTicket, setEditingTicket] = useState(null);
    const [formData, setFormData] = useState({
        name: '',
        ticket_kind: 'online',
        allow_shipping: false,
        description: '',
        base_price: '',
        early_bird_price: '',
        early_bird_until: '',
        vip_surcharge: '0',
        max_quantity: '',
        sales_start: '',
        sales_end: '',
        is_active: true
    });

    const token = localStorage.getItem('token');

    useEffect(() => {
        fetchTickets();
    }, [eventId]);

    const fetchTickets = async () => {
        try {
            const response = await axios.get(`${API_URL}/api/tickets/event/${eventId}`);
            setTickets(response.data);
        } catch (error) {
            console.error('Error fetching tickets:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        const ticketData = {
            event_id: eventId,
            name: formData.name,
            ticket_kind: formData.ticket_kind || 'online',
            allow_shipping: formData.allow_shipping || false,
            description: formData.description || null,
            base_price: parseFloat(formData.base_price),
            early_bird_price: formData.early_bird_price ? parseFloat(formData.early_bird_price) : null,
            early_bird_until: formData.early_bird_until || null,
            vip_surcharge: parseFloat(formData.vip_surcharge) || 0,
            max_quantity: formData.max_quantity ? parseInt(formData.max_quantity) : null,
            sales_start: formData.sales_start || null,
            sales_end: formData.sales_end || null,
            is_active: formData.is_active
        };

        try {
            if (editingTicket) {
                await axios.put(
                    `${API_URL}/api/tickets/types/${editingTicket.id}`,
                    ticketData,
                    { headers: { Authorization: `Bearer ${token}` } }
                );
            } else {
                await axios.post(
                    `${API_URL}/api/tickets/types`,
                    ticketData,
                    { headers: { Authorization: `Bearer ${token}` } }
                );
            }
            
            setIsDialogOpen(false);
            resetForm();
            fetchTickets();
        } catch (error) {
            console.error('Error saving ticket:', error);
            alert('Fehler beim Speichern: ' + (error.response?.data?.detail || error.message));
        }
    };

    const handleEdit = (ticket) => {
        setEditingTicket(ticket);
        setFormData({
            name: ticket.name,
            ticket_kind: ticket.ticket_kind || 'online',
            allow_shipping: ticket.allow_shipping || false,
            description: ticket.description || '',
            base_price: ticket.base_price.toString(),
            early_bird_price: ticket.early_bird_price?.toString() || '',
            early_bird_until: ticket.early_bird_until?.split('T')[0] || '',
            vip_surcharge: ticket.vip_surcharge?.toString() || '0',
            max_quantity: ticket.max_quantity?.toString() || '',
            sales_start: ticket.sales_start?.split('T')[0] || '',
            sales_end: ticket.sales_end?.split('T')[0] || '',
            is_active: ticket.is_active
        });
        setIsDialogOpen(true);
    };

    const handleDelete = async (ticketId) => {
        if (!window.confirm('Diesen Ticket-Typ wirklich löschen?')) return;
        
        try {
            await axios.delete(
                `${API_URL}/api/tickets/types/${ticketId}`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            fetchTickets();
        } catch (error) {
            console.error('Error deleting ticket:', error);
            alert('Fehler beim Löschen');
        }
    };

    const resetForm = () => {
        setEditingTicket(null);
        setFormData({
            name: '',
            ticket_kind: 'online',
            allow_shipping: false,
            description: '',
            base_price: '',
            early_bird_price: '',
            early_bird_until: '',
            vip_surcharge: '0',
            max_quantity: '',
            sales_start: '',
            sales_end: '',
            is_active: true
        });
    };

    const openNewDialog = () => {
        resetForm();
        setIsDialogOpen(true);
    };

    if (loading) {
        return <div className="text-center py-4">Laden...</div>;
    }

    return (
        <div className="space-y-4">
            <div className="flex justify-between items-center">
                <h3 className="text-lg font-bold flex items-center gap-2">
                    <Ticket className="h-5 w-5" />
                    Tickets für: {eventTitle}
                </h3>
                <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
                    <DialogTrigger asChild>
                        <Button onClick={openNewDialog} className="bg-black hover:bg-gray-800">
                            <Plus className="h-4 w-4 mr-2" />
                            Neuer Ticket-Typ
                        </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-lg">
                        <DialogHeader>
                            <DialogTitle>
                                {editingTicket ? 'Ticket-Typ bearbeiten' : 'Neuer Ticket-Typ'}
                            </DialogTitle>
                        </DialogHeader>
                        <form onSubmit={handleSubmit} className="space-y-4">
                            <div>
                                <Label>Name *</Label>
                                <Input
                                    value={formData.name}
                                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                    placeholder="z.B. Standard, VIP, Early Bird"
                                    required
                                />
                            </div>
                            
                            <div>
                                <Label>Beschreibung</Label>
                                <Input
                                    value={formData.description}
                                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                    placeholder="Optional: Was ist enthalten?"
                                />
                            </div>

                            <div>
                                <Label>Ticket-Art</Label>
                                <select
                                    value={formData.ticket_kind}
                                    onChange={(e) => setFormData({ ...formData, ticket_kind: e.target.value })}
                                    className="w-full p-3 border border-gray-200 rounded-lg"
                                >
                                    <option value="online">Online (QR-Ticket per E-Mail)</option>
                                    <option value="physisch">Physisch (Abholung vor Ort)</option>
                                </select>
                                <p className="text-xs text-gray-500 mt-1">
                                    Physisch: kein QR-/Online-Ticket – Abholung am Veranstaltungsort. Zahlung läuft unverändert über Stripe.
                                </p>
                            </div>

                            {formData.ticket_kind === 'physisch' && (
                                <div className="flex items-center space-x-2">
                                    <Switch
                                        checked={formData.allow_shipping}
                                        onCheckedChange={(checked) => setFormData({ ...formData, allow_shipping: checked })}
                                    />
                                    <Label>Versand anbieten (zusätzlich zur Abholung, +2,99 € pauschal)</Label>
                                </div>
                            )}

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <Label>Normalpreis (€) *</Label>
                                    <Input
                                        type="number"
                                        step="0.01"
                                        min="0"
                                        value={formData.base_price}
                                        onChange={(e) => setFormData({ ...formData, base_price: e.target.value })}
                                        placeholder="29.00"
                                        required
                                    />
                                </div>
                                <div>
                                    <Label>VIP-Aufpreis (€)</Label>
                                    <Input
                                        type="number"
                                        step="0.01"
                                        min="0"
                                        value={formData.vip_surcharge}
                                        onChange={(e) => setFormData({ ...formData, vip_surcharge: e.target.value })}
                                        placeholder="0.00"
                                    />
                                </div>
                            </div>
                            
                            <div className="border-t pt-4">
                                <p className="text-sm font-medium mb-2 flex items-center gap-2">
                                    <Calendar className="h-4 w-4" />
                                    Early Bird Konfiguration
                                </p>
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <Label>Early Bird Preis (€)</Label>
                                        <Input
                                            type="number"
                                            step="0.01"
                                            min="0"
                                            value={formData.early_bird_price}
                                            onChange={(e) => setFormData({ ...formData, early_bird_price: e.target.value })}
                                            placeholder="19.00"
                                        />
                                    </div>
                                    <div>
                                        <Label>Gültig bis</Label>
                                        <Input
                                            type="date"
                                            value={formData.early_bird_until}
                                            onChange={(e) => setFormData({ ...formData, early_bird_until: e.target.value })}
                                        />
                                    </div>
                                </div>
                            </div>
                            
                            <div className="border-t pt-4">
                                <p className="text-sm font-medium mb-2 flex items-center gap-2">
                                    <Calendar className="h-4 w-4" />
                                    Verkaufszeitraum
                                </p>
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <Label>Verkauf ab (leer = sofort)</Label>
                                        <Input
                                            type="date"
                                            value={formData.sales_start}
                                            onChange={(e) => setFormData({ ...formData, sales_start: e.target.value })}
                                        />
                                    </div>
                                    <div>
                                        <Label>Verkauf bis (leer = unbegrenzt)</Label>
                                        <Input
                                            type="date"
                                            value={formData.sales_end}
                                            onChange={(e) => setFormData({ ...formData, sales_end: e.target.value })}
                                        />
                                    </div>
                                </div>
                            </div>
                            
                            <div className="border-t pt-4">
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <Label>Max. Anzahl (leer = unbegrenzt)</Label>
                                        <Input
                                            type="number"
                                            min="1"
                                            value={formData.max_quantity}
                                            onChange={(e) => setFormData({ ...formData, max_quantity: e.target.value })}
                                            placeholder="100"
                                        />
                                    </div>
                                    <div className="flex items-center space-x-2 pt-6">
                                        <Switch
                                            checked={formData.is_active}
                                            onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
                                        />
                                        <Label>Aktiv / Verkauf möglich</Label>
                                    </div>
                                </div>
                            </div>
                            
                            <div className="flex justify-end gap-2 pt-4">
                                <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                                    Abbrechen
                                </Button>
                                <Button type="submit" className="bg-black hover:bg-gray-800">
                                    {editingTicket ? 'Speichern' : 'Erstellen'}
                                </Button>
                            </div>
                        </form>
                    </DialogContent>
                </Dialog>
            </div>

            {tickets.length === 0 ? (
                <Card className="border-dashed">
                    <CardContent className="py-8 text-center text-gray-500">
                        <Ticket className="h-12 w-12 mx-auto mb-4 opacity-50" />
                        <p>Noch keine Tickets für dieses Event</p>
                        <p className="text-sm">Klicke auf "Neuer Ticket-Typ" um Tickets anzulegen</p>
                    </CardContent>
                </Card>
            ) : (
                <div className="grid gap-4">
                    {tickets.map((ticket) => (
                        <Card key={ticket.id} className={`${!ticket.is_active ? 'opacity-60' : ''}`}>
                            <CardContent className="p-4">
                                <div className="flex justify-between items-start">
                                    <div className="flex-1">
                                        <div className="flex items-center gap-2 mb-1">
                                            <h4 className="font-bold">{ticket.name}</h4>
                                            {ticket.is_early_bird && (
                                                <span className="bg-green-100 text-green-800 text-xs px-2 py-0.5 rounded">
                                                    Early Bird aktiv
                                                </span>
                                            )}
                                            {!ticket.is_active && (
                                                <span className="bg-red-100 text-red-800 text-xs px-2 py-0.5 rounded">
                                                    Inaktiv
                                                </span>
                                            )}
                                        </div>
                                        {ticket.description && (
                                            <p className="text-sm text-gray-600 mb-2">{ticket.description}</p>
                                        )}
                                        <div className="flex flex-wrap gap-4 text-sm">
                                            <div className="flex items-center gap-1">
                                                <Euro className="h-4 w-4 text-gray-400" />
                                                <span className="font-medium">{ticket.current_price?.toFixed(2)} €</span>
                                                {ticket.is_early_bird && (
                                                    <span className="text-gray-400 line-through ml-1">
                                                        {ticket.base_price?.toFixed(2)} €
                                                    </span>
                                                )}
                                            </div>
                                            <div className="flex items-center gap-1">
                                                <Users className="h-4 w-4 text-gray-400" />
                                                {ticket.available_quantity !== null ? (
                                                    <span>
                                                        {ticket.available_quantity} / {ticket.max_quantity} verfügbar
                                                    </span>
                                                ) : (
                                                    <span>Unbegrenzt</span>
                                                )}
                                            </div>
                                            {ticket.sold_count > 0 && (
                                                <div className="flex items-center gap-1 text-green-600">
                                                    <CheckCircle className="h-4 w-4" />
                                                    <span>{ticket.sold_count} verkauft</span>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                    <div className="flex gap-2">
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={() => handleEdit(ticket)}
                                        >
                                            <Pencil className="h-4 w-4" />
                                        </Button>
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            className="text-red-600 hover:text-red-700"
                                            onClick={() => handleDelete(ticket.id)}
                                        >
                                            <Trash2 className="h-4 w-4" />
                                        </Button>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    ))}
                </div>
            )}
        </div>
    );
};

export default TicketManager;
